'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, Globe, Hash, X } from 'lucide-react'
import { EntryDetailShell, type DetailBadge } from '../EntryDetail/EntryDetailShell'
import { CollapsibleSection } from './CollapsibleSection'
import { CaseEntryDetailPage } from './CaseEntryDetailPage'
import { CampaignGraph } from './CampaignGraph'
import { EntriesTable } from './EntriesTable'
import { apiGet, useCatalog } from './entriesApi'
import Turnstile, { type TurnstileHandle } from '../Turnstile'
import type {
  CampaignDomainIdentifier,
  CampaignEntityReferenceIdentifier,
  EntryDomainLink,
  EntryEntityReferenceLink,
} from './campaignTypes'
import type { EntryRow } from './entryTypes'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { baseDashboardClasses } from '../BaseDashboard'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { consolidateCampaign, ConsolidatedRecord, MergedTimeline, CampaignTargets, type CampaignTargetEvidence } from './CampaignMergedRecord'
import { CampaignReportEvidence } from './CampaignReportEvidence'
import { Chip } from './autoDomainChips'
import { STATUS_LABEL, STATUS_TONE, VERDICT_LABEL, VERDICT_TONE, verdictOf, type DomainSightingRow } from './autoDomainTypes'
import { parseDbTimestamp } from '@/lib/dates'

const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? ''

function fmtShort(iso: string) {
  const d = parseDbTimestamp(iso)
  if (!d) return '—'
  return new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(d)
}

function SidebarBlock({ label, theme, children }: { label: string; theme: ProcessedTheme; children: React.ReactNode }) {
  const c = watchClasses(theme)
  return (
    <section className={`overflow-hidden border ${c.line} ${c.panel}`}>
      <div className={`border-b px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] ${c.line} ${c.label}`}>
        {label}
      </div>
      <div className="p-3">{children}</div>
    </section>
  )
}

type CampaignDetailData = {
  campaignId: string
  campaignType: string | null
  domains: CampaignDomainIdentifier[]
  entityReferences: CampaignEntityReferenceIdentifier[]
  entryCount: number
  entries: EntryRow[]
}

// Same not-a-stable-key caveat as everywhere else this feature touches (see
// campaignTypes.ts) — campaignId here is only ever the one this page opened
// with, resolved fresh by the fetch below. It's fine to keep it in this
// component's own state for the lifetime of one detail view, just never
// carry it out (a bookmark, a stored link) past that.
export function CampaignDetailPage({
  campaignId,
  theme,
  storageKey,
  isEditor,
  onClose,
}: {
  campaignId: string
  theme: ProcessedTheme
  storageKey: string
  isEditor?: boolean
  onClose: () => void
}) {
  const { isDark, muted, border, text } = baseDashboardClasses(theme)
  const wc = watchClasses(theme)
  const { catalog } = useCatalog()

  const [detail, setDetail] = useState<CampaignDetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeIdentifierKey, setActiveIdentifierKey] = useState<string | null>(null)
  const [selectedEntry, setSelectedEntry] = useState<EntryRow | null>(null)
  // Auto-sourced sightings of this campaign's domains (auto_domain_candidate
  // rows whose domain_id is one of ours). The other half of the integration
  // with the Domain Candidates dashboard: there, a known candidate points at
  // this campaign; here, the campaign shows that sourcing re-sighted its
  // domain, when, and what the analysis made of it.
  const [sightings, setSightings] = useState<DomainSightingRow[]>([])

  // Same Turnstile-gated screenshot-token plumbing as UnifiedEntriesDashboardView
  // — CaseEntryDetailPage (opened below on a row click) requires this to let
  // its LatestObtainedImagesPanel fetch evidence screenshots.
  const screenshotTurnstileRef = useRef<TurnstileHandle>(null)
  const pendingTokenRef = useRef<((token: string) => void) | null>(null)
  const handleScreenshotVerify = useCallback((token: string) => {
    pendingTokenRef.current?.(token)
    pendingTokenRef.current = null
  }, [])
  const getScreenshotToken = useCallback((): Promise<string> =>
    new Promise((resolve) => {
      pendingTokenRef.current = resolve
      const tryRun = (attempt = 0) => {
        if (screenshotTurnstileRef.current) {
          screenshotTurnstileRef.current.reset()
          screenshotTurnstileRef.current.run()
          return
        }
        if (attempt < 20) setTimeout(() => tryRun(attempt + 1), 100)
      }
      tryRun()
    }), [])

  const fetchDetail = useCallback(async () => {
    setLoading(true)
    setError(null)
    const result = await apiGet<CampaignDetailData>(`/campaign-groups/detail?campaign_id=${encodeURIComponent(campaignId)}`)
    if (result.success && result.data) {
      setDetail(result.data)
    } else {
      setError(result.error ?? 'Failed to load campaign')
    }
    setLoading(false)
  }, [campaignId])

  useEffect(() => { fetchDetail() }, [fetchDetail])

  useEffect(() => {
    const ids = (detail?.domains ?? []).map((d) => d.domain_id).filter(Boolean)
    if (ids.length === 0) { setSightings([]); return }
    let cancelled = false
    apiGet<DomainSightingRow[]>(`/auto-domains/by-domain?domain_ids=${ids.slice(0, 50).join(',')}`).then((r) => {
      if (!cancelled) setSightings(r.success && Array.isArray(r.data) ? r.data : [])
    })
    return () => { cancelled = true }
  }, [detail])

  // Live/down + probe evidence per reported URL. Separate from the campaign
  // fetch because it reaches into user_entry_domain/entry_browser_data/
  // domain_probe_run rather than the view, and the merged record is readable
  // without it — so it fills in rather than blocking the page.
  const [targetEvidence, setTargetEvidence] = useState<Map<string, CampaignTargetEvidence>>(new Map())
  const [targetsLoading, setTargetsLoading] = useState(false)
  useEffect(() => {
    let cancelled = false
    setTargetEvidence(new Map())
    setTargetsLoading(true)
    apiGet<{ targets: CampaignTargetEvidence[] }>(`/campaign-groups/targets?campaign_id=${encodeURIComponent(campaignId)}`)
      .then((res) => {
        if (cancelled) return
        if (res.success && res.data) {
          setTargetEvidence(new Map(res.data.targets.map((t) => [t.url, t])))
        }
      })
      .finally(() => { if (!cancelled) setTargetsLoading(false) })
    return () => { cancelled = true }
  }, [campaignId])

  // Per-identifier "carried directly by N of M entries" — the count that
  // actually explains why entries are grouped: an identifier every member
  // carries is unremarkable, one carried by a single entry alongside
  // another identifier is the literal bridge (see CampaignGraph's own
  // comment on the same distinction, drawn instead of counted there).
  const directCarrierCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const e of detail?.entries ?? []) {
      const flat = e.processed_entries_flat ?? {}
      const ownDomains: EntryDomainLink[] = flat.entry_domains ?? []
      const ownEntityRefs: EntryEntityReferenceLink[] = flat.entry_entity_references ?? []
      for (const d of ownDomains) {
        const key = `domain:${d.domain_id}`
        counts.set(key, (counts.get(key) ?? 0) + 1)
      }
      for (const r of ownEntityRefs) {
        const key = `entity:${r.entity_reference_id}`
        counts.set(key, (counts.get(key) ?? 0) + 1)
      }
    }
    return counts
  }, [detail])

  const filteredEntries = useMemo(() => {
    if (!detail || !activeIdentifierKey) return detail?.entries ?? []
    const [kind, id] = activeIdentifierKey.split(':')
    return detail.entries.filter((e) => {
      const flat = e.processed_entries_flat ?? {}
      // Matched against the entry's own complete link arrays, never against
      // last_domain_id/last_entity_reference_id — those are the most recent
      // link only, so filtering on them would silently miss entries that
      // carry this identifier alongside a newer one.
      if (kind === 'domain') return (flat.entry_domains as EntryDomainLink[] ?? []).some((d) => d.domain_id === id)
      return (flat.entry_entity_references as EntryEntityReferenceLink[] ?? []).some((r) => r.entity_reference_id === id)
    })
  }, [detail, activeIdentifierKey])

  // Everything the merged view shows is folded out of the member rows this
  // page already fetched — no second request, nothing stored.
  const consolidated = useMemo(() => consolidateCampaign(detail?.entries ?? []), [detail])

  const activeIdentifierLabel = useMemo(() => {
    if (!detail || !activeIdentifierKey) return null
    const [kind, id] = activeIdentifierKey.split(':')
    if (kind === 'domain') return detail.domains.find((d) => d.domain_id === id)?.domain_url ?? null
    const er = detail.entityReferences.find((r) => r.entity_reference_id === id)
    return er ? `${er.entity} / ${er.reference}` : null
  }, [detail, activeIdentifierKey])

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <span className={`font-mono text-[11px] uppercase tracking-widest animate-pulse ${muted}`}>Loading campaign…</span>
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div className={`flex h-64 flex-col items-center justify-center gap-3 text-destructive`}>
        <AlertCircle className="h-5 w-5" />
        <span className="font-mono text-[13px]">{error ?? 'Campaign not found — it may have merged into another one since you opened it. Go back and reopen it from the list.'}</span>
        <button type="button" onClick={onClose} className="font-mono text-[11px] uppercase tracking-widest underline">
          Back to campaigns
        </button>
      </div>
    )
  }

  if (selectedEntry) {
    return (
      <>
        <Turnstile ref={screenshotTurnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleScreenshotVerify} />
        <CaseEntryDetailPage
          entry={selectedEntry}
          theme={theme}
          catalog={catalog}
          onClose={() => setSelectedEntry(null)}
          getScreenshotToken={getScreenshotToken}
          isEditor={isEditor}
          tenantCode={storageKey}
          onDeleted={() => {
            setDetail((prev) => prev ? { ...prev, entries: prev.entries.filter((e) => e.id !== selectedEntry.id) } : prev)
            setSelectedEntry(null)
          }}
          onUpdated={(userEntryId, patch) => {
            setDetail((prev) => prev ? { ...prev, entries: prev.entries.map((e) => (e.id === userEntryId ? { ...e, ...patch } : e)) } : prev)
            setSelectedEntry((prev) => (prev && prev.id === userEntryId ? { ...prev, ...patch } : prev))
          }}
        />
      </>
    )
  }

  const badges: DetailBadge[] = [
    ...(consolidated.anyScam ? [{ label: 'scam', tone: 'red' as const }] : []),
    { label: `${detail.entryCount} ${detail.entryCount === 1 ? 'report' : 'reports'} merged`, tone: 'neutral' },
    ...(detail.domains.length > 0 ? [{ label: `${detail.domains.length} domain${detail.domains.length === 1 ? '' : 's'}`, tone: 'sky' as const }] : []),
    ...(detail.entityReferences.length > 0 ? [{ label: `${detail.entityReferences.length} entity ref${detail.entityReferences.length === 1 ? '' : 's'}`, tone: 'amber' as const }] : []),
  ]

  // Titled by what the campaign actually is — its anchor identifier — rather
  // than by campaign_id, which is a computed key that can change identity
  // when a later entry bridges two components (see campaignTypes.ts). A
  // human-facing campaign NAME/number would need a stable key to hang off,
  // which does not exist yet; deliberately not invented here.
  const primaryIdentifier =
    detail.domains[0]?.domain_url ??
    (detail.entityReferences[0] ? `${detail.entityReferences[0].entity} / ${detail.entityReferences[0].reference}` : null)

  return (
    <>
    {/* Mounted here too, not only on the entry branch above: the per-report
        evidence blocks render LatestObtainedImagesPanel, whose screenshot
        fetch is Turnstile-gated. Without a mounted widget getScreenshotToken
        never resolves and the images just never arrive, silently. */}
    <Turnstile ref={screenshotTurnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleScreenshotVerify} />
    <EntryDetailShell
      mode="page"
      open
      onClose={onClose}
      theme={theme}
      cols={4}
      title={primaryIdentifier ?? detail.campaignType ?? 'Campaign'}
      subtitle={
        `${detail.entryCount} ${detail.entryCount === 1 ? 'report' : 'reports'} merged` +
        (consolidated.firstSeen ? ` · first seen ${fmtShort(consolidated.firstSeen)}` : '') +
        (consolidated.lastSeen ? ` · last activity ${fmtShort(consolidated.lastSeen)}` : '')
      }
      badges={badges}
      sidebar={
        <div className="space-y-3">
          <SidebarBlock label={`Reports in campaign · ${consolidated.reportCount}`} theme={theme}>
            <div className="space-y-1.5">
              {consolidated.ordered.map((e) => {
                const flat = (e.processed_entries_flat ?? {}) as Record<string, any>
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setSelectedEntry(e)}
                    className={`block w-full rounded border px-2 py-1.5 text-left transition-colors hover:border-signal ${wc.line} ${wc.soft}`}
                  >
                    <div className={`font-mono text-[11px] ${wc.text}`}>{String(e.id).slice(0, 8)}</div>
                    <div className={`font-mono text-[10px] ${wc.faint}`}>{flat.source ?? '—'}</div>
                  </button>
                )
              })}
            </div>
          </SidebarBlock>
          <SidebarBlock label="Campaign targets" theme={theme}>
            <CampaignTargets
              data={consolidated}
              theme={theme}
              evidence={targetEvidence}
              evidenceLoading={targetsLoading}
              onSelectDomain={(domainUrl) => {
                const match = detail.domains.find((d) => d.domain_url === domainUrl)
                if (match) setActiveIdentifierKey(`domain:${match.domain_id}`)
              }}
            />
          </SidebarBlock>
        </div>
      }
    >
      <CollapsibleSection label="Consolidated record" theme={theme} defaultOpen>
        <ConsolidatedRecord data={consolidated} theme={theme} />
      </CollapsibleSection>

      <CollapsibleSection label="Merged timeline" theme={theme} defaultOpen>
        <p className={`mb-3 text-[12px] ${wc.muted}`}>
          Every report in this campaign, oldest first — each one's own sender, subject and message, so the campaign reads on its own instead of showing one member's content and a count for the rest.
        </p>
        <MergedTimeline data={consolidated} theme={theme} onOpenEntry={setSelectedEntry} />
      </CollapsibleSection>

      {/* Per report, not campaign-wide: each member has its own
          user_entry_domain link, so its own reported path, browser captures
          and probe runs — a single merged block would have to pick one and
          drop the others' screenshots. Each block is inside its own
          CollapsibleSection, which only mounts children while open, so the
          evidence requests happen on expand rather than all at once. */}
      <CollapsibleSection label={`Domain evidence · per report (${consolidated.reportCount})`} theme={theme} defaultOpen={consolidated.reportCount <= 3}>
        <div className="space-y-3">
          {consolidated.ordered.map((e) => {
            const flat = (e.processed_entries_flat ?? {}) as Record<string, any>
            const urls: string[] = (flat.entry_domains ?? []).map((d: any) => d.original_url ?? d.domain_url).filter(Boolean)
            return (
              <CollapsibleSection
                key={e.id}
                label={`${String(e.id).slice(0, 8)} · ${flat.source ?? 'report'}${urls.length ? ` · ${urls[0]}` : ''}`}
                theme={theme}
                defaultOpen={consolidated.reportCount === 1}
              >
                <CampaignReportEvidence entry={e} theme={theme} getScreenshotToken={getScreenshotToken} />
              </CollapsibleSection>
            )
          })}
        </div>
      </CollapsibleSection>

      {sightings.length > 0 && (
        <CollapsibleSection label={`Auto-sourced sightings · ${sightings.length}`} theme={theme} defaultOpen>
          <p className={`mb-3 text-[12px] ${muted}`}>
            The sourcing side (Domain Candidates) picked up this campaign's domain{detail.domains.length === 1 ? '' : 's'} on its own — each row is one sighting in one sourcing run, with what the analysis found at the time. A promoted sighting is one of the member entries below.
          </p>
          <div className="space-y-1.5">
            {sightings.map((sg) => (
              <div key={sg.candidate_id} data-sighting={sg.candidate_id} className={`flex flex-wrap items-center justify-between gap-2 rounded border px-3 py-2 ${border}`}>
                <div className="flex flex-wrap items-center gap-2 font-mono text-[11px]">
                  <span className={text}>{sg.hostname}</span>
                  <Chip label={STATUS_LABEL[sg.status]} tone={STATUS_TONE[sg.status]} />
                  <Chip label={VERDICT_LABEL[verdictOf(sg)]} tone={VERDICT_TONE[verdictOf(sg)]} />
                  <span className={muted}>{sg.strategy_code ?? '—'} / {sg.gram ?? '—'}</span>
                  {sg.analyzed_at && <span className={muted}>analysed {fmtShort(sg.analyzed_at)}</span>}
                  <span className={muted}>sourced {fmtShort(sg.created_at)}</span>
                </div>
                {sg.platform_name_code && <span className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>{sg.platform_name_code}</span>}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      <CollapsibleSection label="Identifiers" theme={theme} defaultOpen>
        <p className={`mb-3 text-[12px] ${muted}`}>
          Every identifier this campaign spans. A count of 1 means only a single entry carries it directly — that entry is almost always the bridge to whatever else that entry also carries. Click one to filter the entries below to just the ones that carry it.
        </p>
        <div className="flex flex-wrap gap-2">
          {detail.domains.map((d) => {
            const key = `domain:${d.domain_id}`
            const active = activeIdentifierKey === key
            const count = directCarrierCounts.get(key) ?? 0
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveIdentifierKey(active ? null : key)}
                className={`flex items-center gap-1.5 rounded border px-2.5 py-1 font-mono text-[11px] transition-colors ${
                  active ? 'border-signal bg-signal-surface text-signal' : `${border} ${text} hover:border-signal hover:text-signal`
                }`}
              >
                <Globe className="h-3 w-3" />
                {d.domain_url}
                <span className={active ? 'opacity-80' : muted}>· {count}</span>
              </button>
            )
          })}
          {detail.entityReferences.map((r) => {
            const key = `entity:${r.entity_reference_id}`
            const active = activeIdentifierKey === key
            const count = directCarrierCounts.get(key) ?? 0
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveIdentifierKey(active ? null : key)}
                className={`flex items-center gap-1.5 rounded border px-2.5 py-1 font-mono text-[11px] transition-colors ${
                  active ? 'border-warning-border bg-warning-surface text-warning' : `${border} ${text} hover:border-warning-border hover:text-warning`
                }`}
              >
                <Hash className="h-3 w-3" />
                {r.entity} / {r.reference}
                <span className={active ? 'opacity-80' : muted}>· {count}</span>
              </button>
            )
          })}
        </div>
      </CollapsibleSection>

      <CollapsibleSection label="Why these are grouped" theme={theme} defaultOpen>
        <CampaignGraph
          domains={detail.domains}
          entityReferences={detail.entityReferences}
          entries={detail.entries}
          theme={theme}
          activeIdentifierKey={activeIdentifierKey}
          onSelectIdentifier={setActiveIdentifierKey}
        />
      </CollapsibleSection>

      <CollapsibleSection label="Member entries" theme={theme} defaultOpen>
        {/* campaign_entry_count is computed by the view over the whole
            component; detail.entries is what this fetch actually returned.
            They should always agree — if they ever don't, that's a real
            signal (a filtered/truncated fetch), not something to leave the
            operator to notice by counting rows against the header badge. */}
        {detail.entryCount !== detail.entries.length && (
          <p className={`mb-3 text-[12px] text-warning`}>
            This campaign reports {detail.entryCount} member{detail.entryCount === 1 ? '' : 's'}, but {detail.entries.length} {detail.entries.length === 1 ? 'was' : 'were'} loaded — the list below is incomplete.
          </p>
        )}
        <EntriesTable
          entries={filteredEntries}
          theme={theme}
          catalog={catalog}
          total={detail.entries.length}
          onSelectEntry={setSelectedEntry}
          extraToolbar={activeIdentifierLabel && (
            <button
              type="button"
              onClick={() => setActiveIdentifierKey(null)}
              className={`flex shrink-0 items-center gap-1 rounded-[4px] border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${border} ${muted} hover:text-signal`}
            >
              filtered by {activeIdentifierLabel.length > 24 ? `${activeIdentifierLabel.slice(0, 24)}…` : activeIdentifierLabel}
              <X className="h-2.5 w-2.5" />
            </button>
          )}
        />
      </CollapsibleSection>
    </EntryDetailShell>
    </>
  )
}
